<template>
    <page-message v-if="error">
        <template #header>{{t('error')}}</template>
        <template #message>{{error}}</template>
        <template #cta>
            <router-link :to="{name:'home'}" class="btn btn-primary">{{$t('navigation.home')}}</router-link>
        </template>
    </page-message>
    <form v-else class="approvals-employees">
        <layout-two-column>
            <template #column>
                <ApprovalsMenu/>
            </template>

            <template #default>
                <div v-if="!loaded && loading" class="row">
                    <div class="col-12">
                        <loading/>
                    </div>
                </div>

                <div v-if="empty" class="text-center">
                    <div class="pb-3 small">{{t('empty')}}</div>

                    <router-link :to="{name:'home'}" class="btn btn-primary">{{$t('navigation.home')}}</router-link>
                </div>

                <div class="flex-column gap-3" :class="{'d-none':!loaded || empty, 'd-flex':loaded}">
                    <panel class="mb-3">
                        <div class="d-flex gap-3 align-items-center">
                            <input v-model="search"
                                   type="text"
                                   class="form-control"
                                   :disabled="loading"
                                   :placeholder="t('model.search.placeholder')"/>

                            <button class="btn btn-primary" :disabled="loading" @click="load">
                                <span class="d-flex align-items-center flex-nowrap gap-1">
                                    <spinner v-if="search && loading"/>
                                    <span class="icon-search d-lg-none"></span>
                                    <span class="d-none d-lg-inline text-nowrap">{{t('cta.search')}}</span>
                                </span>
                            </button>

                            <button class="btn btn-primary" :disabled="loading || downloading" @click="downloadFile">
                                <span class="d-flex align-items-center flex-nowrap gap-1">
                                    <spinner v-if="downloading"/>
                                    <span class="icon-download d-lg-none"></span>
                                    <span class="d-none d-lg-inline text-nowrap">{{t('cta.download')}}</span>
                                </span>
                            </button>
                        </div>
                    </panel>

                    <div class="overflow-x-auto d-flex flex-column gap-3">
                        <table v-if="!loading && 0 < users.length" class="table small items">
                            <thead>
                            <tr>
                                <th class="name">{{t('users.name')}}</th>
                                <th class="email">{{t('users.email')}}</th>
                                <th class="cta"></th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="user in users" :key="user.id">
                                <td class="name text-break">{{user.name}}</td>
                                <td class="email text-break">{{user.email}}</td>
                                <td class="cta">
                                    <button
                                        v-if="user.hasOrders"
                                        type="button"
                                        class="btn btn-sm btn-link"
                                        @click="showOrders(user.id, user.name, user.email)">
                                        {{t('cta.orders')}}
                                    </button>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                        <div v-if="!loading && 0 === users.length" class="text-center small">{{t('empty')}}</div>
                        <div class="d-flex justify-content-center"
                             :class="{'d-none':loading || 0 === users.length, 'd-flex':!loading}">
                            <pagination v-if="pagination.total"
                                        :default-limit="pagination.limit"
                                        :count="pagination.total"
                                        :disabled="loading"
                                        @refresh="navigate"/>
                        </div>
                    </div>
                </div>
            </template>
        </layout-two-column>
    </form>
</template>

<script>

    import mixin_view from '@/mixins/view';

    import ApprovalsMenu from "@/components/approvals/ApprovalsMenu.vue";
    import LayoutTwoColumn from "@/components/layout/LayoutTwoColumn.vue";
    import ModalApprovalEmployeeOrders from "@/modal/ModalApprovalEmployeeOrders.vue";
    import PageMessage from "@/components/page/PageMessage.vue";
    import Pagination from "@/components/Pagination.vue";
    import Panel from "@/components/Panel";
    import Spinner from "@/components/Spinner.vue";

    export default {
        name: "ViewsEmployees",
        mixins: [mixin_view],
        components: {
            ApprovalsMenu,
            LayoutTwoColumn,
            PageMessage,
            Pagination,
            Panel,
            Spinner
        },
        data() {

            return {
                t_path: "views.approval.employees",
                loaded: false,
                loading: false,
                error: false,
                downloading: false,
                empty: false,
                search: '',
                pagination: {
                    total: null,
                    offset: 0,
                    limit: 20
                },
                users: []
            }
        },
        methods: {
            downloadFile() {

                this.downloading = true

                this.$order
                    .get(`/users/${this.$store.getters["user/id"]}/employees-file`, {responseType: 'blob'})
                    .then((response) => {
                        const href = URL.createObjectURL(response.data);
                        const contentDisposition = response.headers.get('content-disposition')
                        let filename = 'file.xlsx';

                        if (contentDisposition) {

                            const match = response
                                .headers
                                .get('content-disposition')
                                .match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);


                            if (match && match[1]) {
                                filename = match[1].replace(/['"]/g, ''); // remove quotes if any
                            }
                        }

                        const link = document.createElement('a');
                        link.href = href;
                        link.setAttribute('download', filename);
                        document.body.appendChild(link);
                        link.click();

                        document.body.removeChild(link);
                        URL.revokeObjectURL(href);
                    })
                    .finally(() => this.downloading = false)
            },
            load() {

                this.loading = true;

                this.$order
                    .get(`/users/${this.$store.getters["user/id"]}/employees`, {
                        params: {
                            skip: this.pagination.offset,
                            limit: this.pagination.limit,
                            search: this.search
                        }
                    })
                    .then((response) => {

                        this.pagination.total = response.data.pagination.totalCount;
                        this.empty = !this.search?.trim() && 0 === this.pagination.total;

                        this.users = response.data.results.map((user, index) => ({
                            id: user.id,
                            email: user.email,
                            name: user.name,
                            hasOrders: 0 < user.itemCount
                        }))

                        this.loaded = true;
                    })
                    .finally(() => this.loading = false);
            },
            showOrders(employeeId, name, email) {

                this.$modal.show({
                    component: ModalApprovalEmployeeOrders,
                    lg: true,
                    props: {
                        employeeId: employeeId,
                        name: name,
                        email: email,
                    }
                })
            },
            fetch() {

                this.load();
            },
            navigate(pagination) {

                this.pagination.offset = pagination.offset;
                this.pagination.limit = pagination.limit;

                this.load();
            }
        },
        mounted() {

            this.fetch();
            this.view_setTitle();
        }
    }
</script>

<style scoped lang="scss">

</style>